import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, useTheme, useMediaQuery, Alert, CircularProgress, Avatar } from '@mui/material';
import { SearchAndFilterBar, SkeletonLoader } from '../../components/Common';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import StudentForm from '../../components/Forms/StudentForm';
import { studentService, classService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { formatDate } from '../../utils/dateFormat';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { showToast } from '../../utils/toast';

const Alumni = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const filtersRef = useRef({});
  const isInitialLoadRef = useRef(true);
  const hasInitialFetchedRef = useRef(false);
  const [classes, setClasses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  // Server-side infinite scroll state
  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const offsetRef = useRef(0);
  const [isAppending, setIsAppending] = useState(false);
  const lastRequestKeyRef = useRef(null);

  const fetchStudents = useCallback(async (search = '', showSearchLoader = false, append = false, customOffset = null) => {
    if (fetchingRef.current) {
      console.log('fetchStudents: Already fetching, skipping');
      return;
    }
    
    // Create a unique request key to prevent duplicate requests
    const currentFilters = filtersRef.current;
    const nextOffset = customOffset !== null ? customOffset : (append ? offsetRef.current : 0);
    const requestKey = `${search.trim()}_${currentFilters.class_name || ''}_${nextOffset}_${append}`;
    
    // If this is the exact same request as the last one, skip it
    if (lastRequestKeyRef.current === requestKey && !append) {
      console.log('fetchStudents: Duplicate request detected, skipping', requestKey);
      return;
    }
    
    lastRequestKeyRef.current = requestKey;
    fetchingRef.current = true;
    console.log('fetchStudents: Starting fetch', { search, append, customOffset, requestKey });
    const sentinelEl = sentinelRef.current; let io = null; if (sentinelEl && sentinelEl._io) { io = sentinelEl._io; try { io.unobserve(sentinelEl); } catch (e) {} }
    try {
      if (showSearchLoader) { setSearchLoading(true); }
      else if (!append) { setLoading(true); } else { setIsAppending(true); }
      setError(null);

      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (currentFilters.class_name) params.append('class_id', currentFilters.class_name);
      params.append('status', 'inactive');
      params.append('limit', String(pageSize));
      params.append('offset', String(nextOffset));

      const url = `${API_ENDPOINTS.STUDENTS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        const fetched = data.data || [];
        if (append) {
          // Use functional update to avoid needing students in dependencies
          setStudents(prev => [...prev, ...fetched]);
          setFilteredStudents(prev => [...prev, ...fetched]);
        } else {
          setStudents(fetched);
          setFilteredStudents(fetched);
        }
        setHasMore((fetched.length || 0) === pageSize);
        setOffset(nextOffset + (fetched.length || 0));
        offsetRef.current = nextOffset + (fetched.length || 0);
      } else {
        setError(data.error || 'Failed to fetch alumni');
        setHasMore(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch alumni');
      setHasMore(false);
    } finally {
      setTimeout(() => {
        if (showSearchLoader) setSearchLoading(false);
        setLoading(false);
        setIsAppending(false);
        fetchingRef.current = false;
        const el = sentinelRef.current; if (el && el._io) { try { el._io.observe(el); } catch (e) {} }
      }, 0);
    }
  }, []); // No dependencies - use refs for filters

  const fetchClasses = useCallback(async () => {
    try {
      const response = await classService.getAll();
      if (response.success) {
        setClasses(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  }, []);

  // Initialize filtersRef
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initial load - only run once using empty dependency array
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      console.log('Initial load: Already fetched, skipping');
      return;
    }
    hasInitialFetchedRef.current = true;
    isInitialLoadRef.current = false;
    console.log('Initial load: Fetching students and classes');
    setOffset(0); 
    offsetRef.current = 0; 
    setHasMore(true);
    fetchStudents('', false, false, 0);
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount

  // Filter change effect - only run after initial load
  useEffect(() => {
    if (isInitialLoadRef.current) return; // Skip on initial load
    lastRequestKeyRef.current = null; // Reset to allow new request
    setOffset(0); offsetRef.current = 0; setHasMore(true);
    fetchStudents('', false, false, 0);
  }, [filters.class_name]); // Only trigger on filter change

  // Search term effect - only run after initial load
  useEffect(() => {
    if (isInitialLoadRef.current) return; // Skip on initial load
    const timer = setTimeout(() => {
      lastRequestKeyRef.current = null; // Reset to allow new request
      setOffset(0); offsetRef.current = 0; setHasMore(true);
      fetchStudents(searchTerm, true, false, 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]); // Only trigger on search term change

  useEffect(() => {
    setFilteredStudents(students);
  }, [students]);

  // IntersectionObserver for appending
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (!first?.isIntersecting) return;
      if (hasMore && offsetRef.current > 0 && !fetchingRef.current && !loading && !searchLoading) {
        fetchStudents(searchTerm, false, true);
      }
    }, { root: null, rootMargin: '300px', threshold: 0 });
    el._io = io;
    io.observe(el);
    return () => { try { io.disconnect(); } catch (e) {} if (el && el._io === io) el._io = null; };
  }, [hasMore, loading, searchLoading, searchTerm]); // Remove fetchStudents from deps since it's stable now

  const handleSearch = useCallback((term) => setSearchTerm(term), []);
  const handleFilter = useCallback((values) => {
    setFilters(values);
    filtersRef.current = values; // Update ref immediately
  }, []);
  const handleClear = useCallback(() => {
    setSearchTerm('');
    setFilters({});
    filtersRef.current = {}; // Update ref
  }, []);

  const handleView = useCallback((student) => {
    navigate(`/users/alumni/${student.id}`);
  }, [navigate]);

  const handleEdit = useCallback((student) => {
    setEditingStudent(student);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingStudent(null);
  }, []);

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      const response = await fetch(API_ENDPOINTS.STUDENTS, {
        method: formData.id ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        showToast.success('Alumni details updated');
        setModalOpen(false);
        setEditingStudent(null);
        // Reset pagination and refetch from the beginning
        setOffset(0);
        offsetRef.current = 0;
        setHasMore(true);
        lastRequestKeyRef.current = null; // Reset to allow new request
        await fetchStudents('', false, false, 0);
      } else {
        showToast.error(data.error || 'Failed to update alumni');
      }
    } catch (err) {
      showToast.error(err?.message || 'Failed to update alumni');
    } finally {
      setModalLoading(false);
    }
  }, [fetchStudents]);

  const filterOptions = useMemo(() => {
    if (!classes || classes.length === 0) return [];
    return [{
      field: 'class_name',
      label: 'Last Class',
      options: classes.map((cls) => {
        const clsName = cls.name?.startsWith('Class ') ? cls.name : `Class ${cls.name || ''}`;
        const label = cls.section ? `${clsName} - ${cls.section}` : clsName;
        return { value: cls.id, label };
      }),
    }];
  }, [classes]);


  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <SearchAndFilterBar
              title="Alumni"
              onSearch={handleSearch}
              onFilter={handleFilter}
              onClear={handleClear}
              showCreateButton={false}
              searchPlaceholder="Search alumni..."
              filterOptions={filterOptions}
              searchValue={searchTerm}
            />

            <Box sx={{ mt: 2 }}>
              {loading && !searchLoading ? (
                <SkeletonLoader variant="list" count={5} />
              ) : error && !searchLoading ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {filteredStudents.map((row) => (
                    <Paper
                      key={row.id}
                      variant="outlined"
                      sx={{ p: { xs: 2, sm: 2.4 }, borderRadius: 2, borderColor: 'divider', position: 'relative' }}
                    >
                      {/* Status + Actions inline */}
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                        {/* Left: Avatar + Details */}
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Avatar
                            src={row.profile_image || null}
                            alt={row.full_name}
                            sx={{ width: 48, height: 48, bgcolor: getAvatarColor(row.full_name), color: '#ffffff', fontWeight: 700 }}
                          >
                            {!row.profile_image && getInitials(row.full_name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" fontWeight={700}>{row.full_name}</Typography>
                              <Chip size="small" label={'Inactive'} color={'error'} variant="outlined" />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                              <strong>Last Class:</strong> {(row.class_name || '') + (row.class_section ? ` - ${row.class_section}` : '')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                              <strong>Admission:</strong> {formatDate(row.admission_date)} &nbsp;·&nbsp;
                              <strong>Graduated:</strong> {formatDate(row.updated_at)}
                            </Typography>
                            {/* Status moved inline with name */}
                          </Box>
                        </Stack>

                        {/* Right: Actions (match Teachers layout) */}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '100px' }, flexWrap: 'wrap', rowGap: 1 }}>
                          <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleView(row)}>View</Button>
                          <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(row)}>Edit</Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                  {/* Sentinel */}
                  <Box ref={sentinelRef} sx={{ height: 8 }} />
                  {isAppending && (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <StudentForm
              open={modalOpen}
              onClose={handleModalClose}
              onSubmit={handleModalSave}
              initialData={editingStudent}
              title="Edit Alumni"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Alumni;

